import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * SMIN theme provider.
 * ─────────────────────
 * Uses `data-theme="light|dark"` on <html> to switch the CSS variables in
 * index.css. Persists to localStorage("smin_theme"), syncs across open tabs
 * via the `storage` event, and follows the OS preference if the user picks
 * "system".
 *
 * ALSO exports `applyThemeSync()` — call this ONCE at the top of index.js
 * BEFORE React renders to avoid the flash-of-light-theme (FOLT) on refresh.
 */
const KEY = "smin_theme";
const ThemeCtx = createContext({ theme: "light", resolved: "light", setTheme: () => {}, toggle: () => {} });

function readInitial() {
  // Session I-40: SMIN brand mandate is enterprise light-theme across the entire
  // app (marketing + authenticated shell + reports). Any legacy dark preference
  // in localStorage is ignored so every visitor sees the same clean surface.
  return "light";
}

function resolve(_theme) {
  return "light";
}

function apply(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
  // Also keep the legacy .dark / .light class so any Tailwind darkMode:"class"
  // consumers continue to work.
  root.classList.toggle("dark",  resolved === "dark");
  root.classList.toggle("light", resolved === "light");
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#141414" : "#ffffff");
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitial);
  const [resolved, setResolved] = useState(() => resolve(readInitial()));

  useEffect(() => { apply(resolved); }, [resolved]);

  useEffect(() => {
    setResolved(resolve(theme));
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolved(resolve("system"));
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [theme]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== KEY) return;
      const next = readInitial();
      setThemeState(next);
      setResolved(resolve(next));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setTheme = useCallback((_value) => {
    // Locked to light-theme (Session I-40) — no-op.
    setThemeState("light");
    setResolved("light");
  }, []);

  const toggle = useCallback(() => {
    // Locked to light-theme — no-op.
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, resolved, setTheme, toggle }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeCtx);
}

export function applyThemeSync() {
  try {
    const raw = localStorage.getItem(KEY)
      || localStorage.getItem("smin_theme")
      || "light";
    const t = raw === "system"
      ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : (raw === "dark" ? "dark" : "light");
    apply(t);
  } catch {
    apply("light");
  }
}
